import { useCallback } from "react";

import { type RegisterOnboardingUploadInput } from "~/gql/graphqlBase.generated";
import { isQueuedNetworkFailure } from "~/helpers/offline/classifyMutationFireOutcome";
import { type MutationRawResult } from "~/helpers/runMutation";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useRegisterOnboardingUploadMutation,
	useStartOrResumeOnboardingJourneyMutation,
	useUploadOnboardingFileMutation
} from "~/services/endpoints/apiEndpoints";
import { type OnboardingUploadResult } from "~/services/endpoints/onboardingUploadApi";

export type UploadRegistrationOutcome = "refused" | "registered" | "unreachable";

export interface UseOnboardingJourneyMutationsReturn {
	isStartingJourney: boolean;
	registerUpload: (input: RegisterOnboardingUploadInput) => Promise<UploadRegistrationOutcome>;
	startJourney: (sessionReference?: string | null) => Promise<string | null>;
	uploadFile: (file: File) => Promise<MutationRawResult<OnboardingUploadResult>>;
}

const ENTITY_LABEL = "Setup file";

const START_ACTION = "starting your setup";

const useOnboardingJourneyMutations = (): UseOnboardingJourneyMutationsReturn => {
	const { runForEntity, runForResult } = useMutationRunner();
	const [registerMutation] = useRegisterOnboardingUploadMutation();
	const [startMutation, { isLoading: isStartingJourney }] = useStartOrResumeOnboardingJourneyMutation();
	const [uploadMutation] = useUploadOnboardingFileMutation();

	const registerUpload = useCallback(
		async (input: RegisterOnboardingUploadInput): Promise<UploadRegistrationOutcome> => {
			const result = await runForResult(
				registerMutation({ input }),
				(data) => data.registerOnboardingUpload,
				snackMessages.addError(ENTITY_LABEL),
				{ expectedError: isQueuedNetworkFailure }
			);
			if (result.payloadErrors?.length) return "refused";
			if (!result.error) return "registered";
			const isLostConnection = isQueuedNetworkFailure(result.error);
			return isLostConnection ? "unreachable" : "refused";
		},
		[registerMutation, runForResult]
	);

	const startJourney = useCallback(
		async (sessionReference?: string | null) => {
			const journeyStart = await runForEntity(
				startMutation({ input: sessionReference ? { routing: { sessionReference } } : {} }),
				(data) => data.startOrResumeOnboardingJourney,
				(payload) => payload.result,
				snackMessages.actionError(START_ACTION)
			);
			return journeyStart?.journeyId ?? null;
		},
		[runForEntity, startMutation]
	);

	const uploadFile = useCallback(
		(file: File) =>
			runForResult(uploadMutation(file), (data) => ({ ...data, errors: null }), snackMessages.addError(ENTITY_LABEL), {
				silent: true
			}),
		[runForResult, uploadMutation]
	);

	return { isStartingJourney, registerUpload, startJourney, uploadFile };
};

export default useOnboardingJourneyMutations;
